import { useGetList, useListContext } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CircleMinus, CirclePlus } from "lucide-react";

type TagState = "off" | "include" | "exclude";

/**
 * Multi-select tag filter buttons. Each tag cycles through three states:
 * - off: no filter applied
 * - include: contacts must have this tag (tags@cs)
 * - exclude: contacts must NOT have this tag (tags@not.cs)
 */
export const TagFilterButtons = ({
  size,
  className,
}: {
  size?: "default" | "sm" | "lg" | "icon" | null;
  className?: string;
}) => {
  const { filterValues, setFilters } = useListContext();
  const { data: tags } = useGetList("tags", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "name", order: "ASC" },
  });

  const includedIds = parseTagIds(filterValues["tags@cs"]);
  const excludedIds = parseTagIds(filterValues["tags@not.cs"]);

  const getTagState = (tagId: number): TagState => {
    if (includedIds.includes(tagId)) return "include";
    if (excludedIds.includes(tagId)) return "exclude";
    return "off";
  };

  const handleClick = (tagId: number) => {
    const state = getTagState(tagId);
    const nextState: TagState =
      state === "off" ? "include" : state === "include" ? "exclude" : "off";

    const newIncluded = includedIds.filter((id) => id !== tagId);
    const newExcluded = excludedIds.filter((id) => id !== tagId);

    if (nextState === "include") newIncluded.push(tagId);
    if (nextState === "exclude") newExcluded.push(tagId);

    setFilters({
      ...filterValues,
      "tags@cs": newIncluded.length > 0 ? formatTagIds(newIncluded) : undefined,
      "tags@not.cs":
        newExcluded.length > 0 ? formatTagIds(newExcluded) : undefined,
    });
  };

  if (!tags) return null;

  return (
    <>
      {tags.map((tag) => {
        const state = getTagState(tag.id);
        return (
          <Button
            key={tag.id}
            variant={state === "off" ? "ghost" : "secondary"}
            onClick={() => handleClick(tag.id)}
            className={cn(
              "cursor-pointer flex flex-row items-center justify-between gap-2 px-2.5 w-full",
              state === "exclude" &&
                "bg-destructive/10 hover:bg-destructive/20",
              className,
            )}
            size={size ?? "sm"}
          >
            <Badge
              variant="secondary"
              className={cn(
                "text-black text-sm md:text-xs font-normal cursor-pointer",
                state === "exclude" && "line-through opacity-70",
              )}
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
            </Badge>
            {state === "include" && (
              <CirclePlus className="opacity-50 size-4" />
            )}
            {state === "exclude" && (
              <CircleMinus className="opacity-50 size-4" />
            )}
          </Button>
        );
      })}
    </>
  );
};

/**
 * Active tag filter badges for the filter summary.
 */
export const ActiveTagFilters = ({ className }: { className?: string }) => {
  const { filterValues, setFilters } = useListContext();
  const { data: tags } = useGetList("tags", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "name", order: "ASC" },
  });

  const includedIds = parseTagIds(filterValues["tags@cs"]);
  const excludedIds = parseTagIds(filterValues["tags@not.cs"]);

  if (!tags || (includedIds.length === 0 && excludedIds.length === 0))
    return null;

  const removeTag = (tagId: number, type: "include" | "exclude") => {
    const key = type === "include" ? "tags@cs" : "tags@not.cs";
    const ids = parseTagIds(filterValues[key]).filter((id) => id !== tagId);
    setFilters({
      ...filterValues,
      [key]: ids.length > 0 ? formatTagIds(ids) : undefined,
    });
  };

  return (
    <>
      {tags
        .filter((tag) => includedIds.includes(tag.id))
        .map((tag) => (
          <Button
            key={`inc-${tag.id}`}
            variant="secondary"
            onClick={() => removeTag(tag.id, "include")}
            className={cn(
              "cursor-pointer flex flex-row items-center justify-between gap-2 px-2 w-auto h-8",
              className,
            )}
            size="sm"
          >
            <Badge
              variant="secondary"
              className="text-black text-sm md:text-xs font-normal cursor-pointer"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
            </Badge>
            <CirclePlus className="opacity-50 size-4" />
          </Button>
        ))}
      {tags
        .filter((tag) => excludedIds.includes(tag.id))
        .map((tag) => (
          <Button
            key={`exc-${tag.id}`}
            variant="secondary"
            onClick={() => removeTag(tag.id, "exclude")}
            className={cn(
              "cursor-pointer flex flex-row items-center justify-between gap-2 px-2 w-auto h-8 bg-destructive/10 hover:bg-destructive/20",
              className,
            )}
            size="sm"
          >
            <Badge
              variant="secondary"
              className="text-black text-sm md:text-xs font-normal cursor-pointer line-through opacity-70"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
            </Badge>
            <CircleMinus className="opacity-50 size-4" />
          </Button>
        ))}
    </>
  );
};

function parseTagIds(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .slice(1, -1)
    .split(",")
    .map(Number)
    .filter((n) => !isNaN(n));
}

function formatTagIds(ids: number[]): string {
  return `{${ids.join(",")}}`;
}
